export type BusinessCategoryLike = {
  id?: string | number | null;
  name?: string | null;
  slug?: string | null;
  parentCategoryId?: string | number | null;
  parentCategory?: BusinessCategoryLike | null;
};

export function isHealthcareCategoryName(name?: string | null): boolean {
  const normalized = String(name || "").trim().toLowerCase();
  return normalized === "healthcare" || normalized === "health care";
}

export function isAdultFamilyHomeCategoryName(name?: string | null): boolean {
  return /adult\s*family\s*home/i.test(String(name || ""));
}

export function isHealthcareSubcategoryName(name?: string | null): boolean {
  return /adult\s*family\s*home|assisted\s*living|independent\s*living|home\s*care|senior\s*living|memory\s*care|nursing\s*home|pharmacy|clinic|medical|dental|therapy|urgent\s*care|home\s*health|hospice|mental\s*health|behavioral\s*health|rehab|rehabilitation|physician|hospital|chiropractic|optometry|vision/i.test(
    String(name || "")
  );
}

export function isCareCategoryName(name?: string | null): boolean {
  return /health\s*care|adult\s*family\s*home|assisted\s*living|independent\s*living|home\s*care|senior\s*living|memory\s*care|nursing\s*home/i.test(
    String(name || "")
  );
}

export function isCareBusinessCategory(
  category?: BusinessCategoryLike | null
): boolean {
  if (!category) return false;
  return (
    isCareCategoryName(category?.name) ||
    isCareCategoryName(category?.parentCategory?.name) ||
    String(category?.slug || "").toLowerCase() === "healthcare" ||
    String(category?.parentCategory?.slug || "").toLowerCase() === "healthcare"
  );
}

export function isCareBusinessEligible(
  category?: BusinessCategoryLike | null,
  business?: any
): boolean {
  return (
    isCareBusinessCategory(category) ||
    Boolean(business?.careSettings?.afhLicenseNumber) ||
    Boolean(business?.businessBasic?.careSettings?.afhLicenseNumber)
  );
}

export function isTopLevelBusinessCategory(
  category?: BusinessCategoryLike | null
): boolean {
  return !category?.parentCategoryId;
}

export function getTopLevelBusinessCategory(
  category?: BusinessCategoryLike | null
): BusinessCategoryLike | null {
  if (!category) return null;
  return category?.parentCategory ? category.parentCategory : category;
}

export function getBusinessSubcategory(
  category?: BusinessCategoryLike | null
): BusinessCategoryLike | null {
  if (!category?.parentCategoryId) return null;
  return category;
}

export function getTopLevelBusinessCategories<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined
): T[] {
  return (Array.isArray(categories) ? categories : []).filter((category) =>
    isTopLevelBusinessCategory(category)
  );
}

export function isHealthcareBusinessCategory(
  category?: BusinessCategoryLike | null
): boolean {
  if (!category) return false;
  return (
    isHealthcareCategoryName(category?.name) ||
    isHealthcareSubcategoryName(category?.name) ||
    isHealthcareCategoryName(category?.parentCategory?.name) ||
    isHealthcareSubcategoryName(category?.parentCategory?.name) ||
    String(category?.slug || "").toLowerCase() === "healthcare" ||
    String(category?.parentCategory?.slug || "").toLowerCase() === "healthcare"
  );
}

function hasExplicitBusinessHierarchy<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined
): boolean {
  return (Array.isArray(categories) ? categories : []).some(
    (category) =>
      Number(category?.parentCategoryId || 0) > 0 || Boolean(category?.parentCategory)
  );
}

export function getBusinessSubcategories<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined,
  parentCategoryId?: string | number | null
): T[] {
  const parentId = Number(parentCategoryId || 0);
  if (!parentId) return [];
  return (Array.isArray(categories) ? categories : []).filter(
    (category) => Number(category?.parentCategoryId) === parentId
  );
}

export function formatBusinessCategoryPath(
  category?: BusinessCategoryLike | null
): string {
  if (!category?.name) return "";
  const parentName = category?.parentCategory?.name;
  return parentName ? `${parentName} / ${category.name}` : String(category.name);
}

export function filterBusinessCategoriesForProduct<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined,
  productId?: string | null
): T[] {
  const list = Array.isArray(categories) ? categories : [];
  if (productId !== "care") return list;
  return list.filter((category) => isCareBusinessCategory(category));
}

export function getHealthcareTopLevelBusinessCategory<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined
): T | null {
  return (
    (Array.isArray(categories) ? categories : []).find(
      (category) =>
        isHealthcareCategoryName(category?.name) ||
        String(category?.slug || "").toLowerCase() === "healthcare"
    ) || null
  );
}

export function getDisplayedBusinessTopLevelCategories<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined,
  productId?: string | null
): T[] {
  const filteredCategories = filterBusinessCategoriesForProduct(categories, productId);
  if (hasExplicitBusinessHierarchy(filteredCategories)) {
    return getTopLevelBusinessCategories(filteredCategories);
  }

  const healthcareCategory = getHealthcareTopLevelBusinessCategory(filteredCategories);
  if (!healthcareCategory) {
    if (productId === "care") {
      return [...filteredCategories].sort((left, right) =>
        String(left?.name || "").localeCompare(String(right?.name || ""))
      );
    }
    return getTopLevelBusinessCategories(filteredCategories);
  }

  return getTopLevelBusinessCategories(filteredCategories)
    .filter(
      (category) =>
        Number(category?.id || 0) === Number(healthcareCategory?.id || 0) ||
        !isHealthcareBusinessCategory(category)
    )
    .sort((left, right) => {
      const leftRank =
        Number(left?.id || 0) === Number(healthcareCategory?.id || 0) ? 0 : 1;
      const rightRank =
        Number(right?.id || 0) === Number(healthcareCategory?.id || 0) ? 0 : 1;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return String(left?.name || "").localeCompare(String(right?.name || ""));
    });
}

export function getDisplayedBusinessSubcategories<T extends BusinessCategoryLike>(
  categories: T[] | null | undefined,
  parentCategoryId?: string | number | null,
  productId?: string | null
): T[] {
  const filteredCategories = filterBusinessCategoriesForProduct(categories, productId);
  const realSubcategories = getBusinessSubcategories(
    filteredCategories,
    parentCategoryId
  );
  if (realSubcategories.length > 0 || hasExplicitBusinessHierarchy(filteredCategories)) {
    return realSubcategories;
  }

  const healthcareCategory = getHealthcareTopLevelBusinessCategory(filteredCategories);
  if (Number(parentCategoryId || 0) !== Number(healthcareCategory?.id || 0)) {
    return [];
  }

  return filteredCategories
    .filter(
      (category) =>
        Number(category?.id || 0) !== Number(healthcareCategory?.id || 0) &&
        isHealthcareBusinessCategory(category) &&
        !isHealthcareCategoryName(category?.name)
    )
    .sort((left, right) =>
      String(left?.name || "").localeCompare(String(right?.name || ""))
    );
}
